import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    ReactNode,
    HTMLAttributes,
} from "react"
import { createPortal } from "react-dom"
import { zIndex } from "@nova/constants"
import classNames from "classnames"

export type DrawerDirection = "left" | "right" | "top" | "bottom"

const DrawerContext = createContext<{
    isOpen: boolean
    setIsOpen: (isOpen: boolean) => void
} | null>(null)

const useDrawer = () => {
    const ctx = useContext(DrawerContext)
    if (!ctx) throw new Error("useDrawer must be used within <Drawer>")
    return ctx
}

const Root = ({
    children,
    onOpenChange,
}: {
    children: ReactNode
    onOpenChange?: (isOpen: boolean) => void
}) => {
    const [isOpen, setIsOpen] = useState(false)

    const setOpen = useCallback(
        (next: boolean) => {
            setIsOpen(next)
            onOpenChange?.(next)
        },
        [onOpenChange]
    )

    return (
        <DrawerContext.Provider value={{ isOpen, setIsOpen: setOpen }}>
            {children}
        </DrawerContext.Provider>
    )
}

const Trigger = ({ children }: { children: ReactNode }) => {
    const { setIsOpen } = useDrawer()
    return <div onClick={() => setIsOpen(true)}>{children}</div>
}

const Close = ({ children }: { children: ReactNode }) => {
    const { setIsOpen } = useDrawer()
    return <div onClick={() => setIsOpen(false)}>{children}</div>
}

const closedTransform: Record<DrawerDirection, string> = {
    left: "-translate-x-full",
    right: "translate-x-full",
    top: "-translate-y-full",
    bottom: "translate-y-full",
}

const anchor: Record<DrawerDirection, string> = {
    left: "top-0 left-0 h-full",
    right: "top-0 right-0 h-full",
    top: "top-0 left-0 w-full",
    bottom: "bottom-0 left-0 w-full",
}

const outerBorder: Record<DrawerDirection, string> = {
    left: "border-r border-white-200",
    right: "border-l border-white-200",
    top: "border-b border-white-200",
    bottom: "border-t border-white-200",
}

const Menu = ({
    children,
    direction = "right",
    className,
    ...props
}: HTMLAttributes<HTMLDivElement> & { direction?: DrawerDirection }) => {
    const { isOpen, setIsOpen } = useDrawer()

    useEffect(() => {
        if (!isOpen) return
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") setIsOpen(false)
        }
        document.addEventListener("keydown", closeOnEscape)
        return () => document.removeEventListener("keydown", closeOnEscape)
    }, [isOpen, setIsOpen])

    return createPortal(
        <div
            className="fixed inset-0"
            style={{ zIndex: zIndex.HighPriorityModal, pointerEvents: isOpen ? "auto" : "none" }}
            aria-hidden={!isOpen}
        >
            <div
                onClick={() => setIsOpen(false)}
                className={classNames(
                    "absolute inset-0 bg-black-800 transition-opacity duration-300",
                    isOpen ? "opacity-100" : "opacity-0"
                )}
            />
            <div
                {...props}
                role="dialog"
                aria-modal={isOpen}
                className={classNames(
                    "absolute bg-black-800 shadow-dp4 transition-transform duration-300 ease-in-out",
                    anchor[direction],
                    outerBorder[direction],
                    isOpen ? "translate-x-0 translate-y-0" : closedTransform[direction],
                    className
                )}
            >
                {children}
            </div>
        </div>,
        document.getElementById("portal")!
    )
}

export const Drawer = Object.assign(Root, { Trigger, Menu, Close })
